import { useState } from "react";
import { supabase } from "../../lib/supabase/client";
import { useAuth } from "../../context/AuthContext";

export default function Login() {
  const { domainError, authMethods } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  async function handleOAuthSignIn(provider) {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    });
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    if (data?.url) window.location.href = data.url;
  }

  async function handleMagicLink() {
    if (!magicEmail.trim()) return;
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: magicEmail.trim(),
      options: { emailRedirectTo: window.location.origin },
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setMagicSent(true);
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logoWrap}>
          <img src="/khave-logo.jpg" alt="KHĀVE" style={s.logoImg} />
          <div>
            <div style={s.wordmark}>KHĀVE<sup style={s.reg}>®</sup></div>
            <div style={s.tagline}>COMMAND PORTAL</div>
          </div>
        </div>
        <div style={s.divider} />
        <h1 style={s.heading}>Welcome back</h1>
        <p style={s.subtext}>Sign in to access the KHĀVE operations portal.</p>

        {/* Google — always rendered as fallback */}
        <button onClick={() => handleOAuthSignIn("google")} disabled={loading} style={{ ...s.oauthBtn, opacity: loading ? 0.7 : 1 }}>
          {!loading && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#fff" fillOpacity=".9"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#fff" fillOpacity=".75"/>
              <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#fff" fillOpacity=".6"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#fff" fillOpacity=".45"/>
            </svg>
          )}
          {loading ? "Redirecting…" : "Sign in with Google"}
        </button>

        {/* Microsoft — conditional */}
        {authMethods?.microsoft && (
          <button onClick={() => handleOAuthSignIn("azure")} disabled={loading} style={{ ...s.oauthBtn, ...s.secondaryBtn, marginTop: 10 }}>
            Sign in with Microsoft
          </button>
        )}

        {/* Apple — conditional */}
        {authMethods?.apple && (
          <button onClick={() => handleOAuthSignIn("apple")} disabled={loading} style={{ ...s.oauthBtn, ...s.secondaryBtn, marginTop: 10 }}>
            Sign in with Apple
          </button>
        )}

        {/* Magic link — conditional */}
        {authMethods?.magicLink && (
          <div style={s.magicSection}>
            <div style={s.orDivider}><span style={s.orText}>or</span></div>
            {magicSent ? (
              <p style={s.magicSent}>Check your email for a sign-in link.</p>
            ) : (
              <div style={s.magicRow}>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={magicEmail}
                  onChange={e => setMagicEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleMagicLink()}
                  style={s.magicInput}
                />
                <button onClick={handleMagicLink} disabled={loading || !magicEmail.trim()} style={s.magicBtn}>
                  Send link
                </button>
              </div>
            )}
          </div>
        )}

        {(error || domainError) && (
          <p style={s.errorMsg}>{domainError || error}</p>
        )}

        <p style={s.footerNote}>
          Access is restricted to authorised team members only.
        </p>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", background: "var(--khv-bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  card: { width: 420, maxWidth: "100%", background: "var(--khv-surface)", borderRadius: "var(--radius-lg)", padding: "40px 36px 32px", boxShadow: "var(--khv-shadow-lg)", border: "1px solid var(--khv-border)" },
  logoWrap: { display: "flex", alignItems: "center", gap: 14, marginBottom: 24 },
  logoImg: { width: 44, height: 44, borderRadius: 10, objectFit: "cover", flexShrink: 0 },
  wordmark: { fontSize: 20, fontWeight: 800, color: "var(--khv-text-primary)", letterSpacing: 2, lineHeight: 1 },
  reg: { fontSize: 10, fontWeight: 600, verticalAlign: "super", letterSpacing: 0 },
  tagline: { fontSize: 9, fontWeight: 700, letterSpacing: 2.5, color: "var(--khv-text-muted)", marginTop: 3 },
  divider: { height: 1, background: "var(--khv-border)", marginBottom: 28 },
  heading: { margin: "0 0 8px", fontSize: 22, fontWeight: 800, color: "var(--khv-text-primary)" },
  subtext: { margin: "0 0 28px", fontSize: 13, color: "var(--khv-text-secondary)", lineHeight: 1.5 },
  oauthBtn: { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "13px 20px", borderRadius: "var(--radius-md)", border: "none", background: "var(--khv-accent)", color: "var(--khv-text-inverse)", fontSize: 14, fontWeight: 700, cursor: "pointer", letterSpacing: 0.2, transition: "opacity .15s" },
  secondaryBtn: { background: "var(--khv-surface)", color: "var(--khv-text-primary)", border: "1.5px solid var(--khv-border-strong)" },
  magicSection: { marginTop: 16 },
  orDivider: { textAlign: "center", margin: "12px 0", borderBottom: "1px solid var(--khv-border)", lineHeight: 0 },
  orText: { background: "var(--khv-surface)", padding: "0 12px", fontSize: 11, color: "var(--khv-text-muted)", fontWeight: 600 },
  magicRow: { display: "flex", gap: 8 },
  magicInput: { flex: 1, padding: "10px 12px", borderRadius: "var(--radius-md)", border: "1.5px solid var(--khv-border-strong)", fontSize: 13, outline: "none", color: "var(--khv-text-primary)", background: "var(--khv-surface)" },
  magicBtn: { padding: "10px 16px", borderRadius: "var(--radius-md)", border: "none", background: "var(--khv-accent)", color: "var(--khv-text-inverse)", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" },
  magicSent: { fontSize: 13, color: "var(--khv-status-complete)", textAlign: "center", fontWeight: 600 },
  errorMsg: { marginTop: 14, fontSize: 12, color: "var(--khv-status-overdue)", textAlign: "center" },
  footerNote: { marginTop: 24, fontSize: 11, color: "var(--khv-text-muted)", textAlign: "center", lineHeight: 1.5 },
};
